import { useEffect, useRef, useState } from 'react';
import { QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Toaster, toast } from 'sonner';
import { MessageSquareText, Moon, Search, Sun } from 'lucide-react';
import { displayName, parseMailbox } from '@dts/core';
import type { PublicSession } from '@/bridge/types';
import { queryClient } from './lib/query-client';
import { useUi } from './lib/store';
import { useFolders } from './lib/queries';
import { composeMailto, composeNew, composeWithAi } from './lib/compose';
import { wireCrmEvents } from './lib/crm';
import { DownloadBar } from './components/DownloadBar';
import { Sidebar } from './components/Sidebar';
import { ComposerDock } from './components/Composer';
import { CommandPalette } from './components/CommandPalette';
import { AskPanel } from './components/AskPanel';
import { CrmApprovalDialog, CrmPanel } from './components/CrmPanel';
import { DialogHost } from './components/Dialogs';
import { Kbd, Spinner } from './components/ui';
import { Login } from './views/Login';
import { Today } from './views/Today';
import { Mail } from './views/Mail';
import { Contacts } from './views/Contacts';
import { Campaigns } from './views/Campaigns';
import { Settings } from './views/Settings';

function useTheme() {
  const theme = useUi((s) => s.local.theme);
  const hue = useUi((s) => s.local.hue);
  useEffect(() => {
    const apply = () => {
      const dark = theme === 'dark' || (theme === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
      document.documentElement.classList.toggle('dark', dark);
      window.dts.app.setTheme(dark ? 'dark' : 'light');
    };
    apply();
    const mq = matchMedia('(prefers-color-scheme: dark)');
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, [theme]);
  useEffect(() => document.documentElement.style.setProperty('--hue', String(hue)), [hue]);
}

function Aurora() {
  return (
    <>
      <div className="aurora">
        <span style={{ left: '5%', top: '0%', width: '45vw', height: '45vw', background: 'radial-gradient(circle, var(--ai-1), transparent 65%)' }} />
        <span style={{ right: '0%', top: '20%', width: '40vw', height: '40vw', background: 'radial-gradient(circle, var(--ai-2), transparent 65%)', animationDelay: '-6s', opacity: 0.6 }} />
        <span style={{ left: '35%', bottom: '-10%', width: '50vw', height: '35vw', background: 'radial-gradient(circle, var(--ai-3), transparent 65%)', animationDelay: '-12s', opacity: 0.4 }} />
      </div>
      <div className="noise" />
    </>
  );
}

function TopBar() {
  const view = useUi((s) => s.view);
  const setView = useUi((s) => s.setView);
  const openAsk = useUi((s) => s.openAsk);
  const setPalette = useUi((s) => s.setPalette);
  const local = useUi((s) => s.local);
  const setLocal = useUi((s) => s.setLocal);
  const [q, setQ] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const isMac = /Mac|iPhone|iPad/.test(navigator.userAgent);

  useEffect(() => {
    const focus = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === '/' && !t.isContentEditable && !/INPUT|TEXTAREA/.test(t.tagName)) {
        e.preventDefault();
        input.current?.focus();
      }
    };
    window.addEventListener('keydown', focus);
    return () => window.removeEventListener('keydown', focus);
  }, []);

  useEffect(() => {
    if (view.kind !== 'mail' || !view.search) setQ('');
  }, [view]);

  const submit = () => {
    const v = q.trim();
    if (!v) return;
    // Questions go to the AI; keywords search.
    if (/\?$/.test(v) || /^(what|who|when|where|why|how|did|do|does|is|are|any|show|find|summari[sz]e|list)\b/i.test(v)) openAsk(v);
    else setView({ kind: 'mail', search: v });
    input.current?.blur();
  };

  return (
    <div className={`drag relative z-20 flex h-[52px] shrink-0 items-center gap-3 px-4 `}>
      <div className="flex-1" />
      <div className="no-drag group flex h-9 w-[min(560px,48vw)] items-center gap-2.5 rounded-xl border border-line bg-raised/50 px-3 transition focus-within:border-accent/50 focus-within:bg-raised focus-within:ring-4 focus-within:ring-accent/10">
        <Search className="size-4 text-faint" />
        <input
          ref={input}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
            if (e.key === 'Escape') (setQ(''), input.current?.blur());
          }}
          placeholder="Search mail, or ask a question…"
          className="h-full flex-1 bg-transparent text-[13.5px] outline-none placeholder:text-faint"
        />
        <button onClick={() => setPalette(true)} className="flex items-center gap-1"><Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd><Kbd>K</Kbd></button>
      </div>
      <button onClick={() => openAsk()} className="no-drag btn-legend ai-gradient flex h-9 items-center gap-2 rounded-xl px-3.5 text-[13px] font-semibold text-white shadow-lg glow transition hover:brightness-110 active:scale-95">
        <MessageSquareText className="size-4" /> Ask AI
      </button>
      <button onClick={() => setLocal({ theme: local.theme === 'dark' ? 'light' : 'dark' })} className="no-drag flex size-9 items-center justify-center rounded-xl text-muted transition hover:bg-line hover:text-fg" title="Toggle theme">
        {local.theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </button>
      <div className="flex-1" />
    </div>
  );
}

/** Taskbar/dock badge for unread Inbox mail. */
function BadgeWatcher() {
  const unread = useFolders().data?.find((f) => f.name === 'Inbox')?.unread ?? 0;
  useEffect(() => {
    let overlay: string | undefined;
    if (unread > 0 && window.dts.platform === 'win32') {
      const c = document.createElement('canvas');
      c.width = c.height = 32;
      const g = c.getContext('2d')!;
      g.fillStyle = '#e11d48';
      g.beginPath();
      g.arc(16, 16, 16, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = '#fff';
      g.font = `bold ${unread > 99 ? 13 : 18}px Segoe UI, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      g.fillText(unread > 99 ? '99+' : String(unread), 16, 17);
      overlay = c.toDataURL();
    }
    window.dts.app.setBadge(unread, overlay);
  }, [unread]);
  return null;
}

/**
 * Server push: events arrive from this device's Storage Queue (polled by the main process) and refresh exactly
 * the data that changed. New mail shows an in-app toast when the window is focused (native notification otherwise).
 */
function useEventSync() {
  const qc = useQueryClient();
  const notifications = useUi((s) => s.local.notifications);
  const setView = useUi((s) => s.setView);
  const select = useUi((s) => s.select);
  // New mail arriving in a burst (an import, a backlog draining from the queue) shares one toast that counts up,
  // instead of stacking one per message; the count starts over once that toast closes.
  const burst = useRef(0);
  useEffect(() => window.dts.events.setNotifications(notifications), [notifications]);
  useEffect(() => {
    const open = (id: string, folder?: string) => {
      setView({ kind: 'mail', folder: folder ?? 'Inbox' });
      select(id);
    };
    const offEvent = window.dts.events.onEvent((e) => {
      switch (e.type) {
        case 'mail.received':
          qc.invalidateQueries({ queryKey: ['messages'] });
          qc.invalidateQueries({ queryKey: ['folders'] });
          qc.invalidateQueries({ queryKey: ['brief'] });
          if (notifications && document.hasFocus() && e.data && !e.data.isJunk) {
            const from = displayName(parseMailbox(e.data.from ?? '') ?? { address: e.data.fromAddress ?? '' });
            const n = ++burst.current;
            const reset = () => (burst.current = 0);
            if (n === 1) {
              toast(from || 'New email', { id: 'new-mail', description: e.data.subject, action: { label: 'Open', onClick: () => open(e.data.id, e.data.folder) }, onDismiss: reset, onAutoClose: reset });
            } else {
              toast(`${n} new emails`, {
                id: 'new-mail',
                description: `Latest: ${from || 'New email'}${e.data.subject ? ` — ${e.data.subject}` : ''}`,
                action: { label: 'Open Inbox', onClick: () => setView({ kind: 'mail', folder: 'Inbox' }) },
                onDismiss: reset,
                onAutoClose: reset,
              });
            }
          }
          break;
        case 'mail.updated':
        case 'mail.sent':
        case 'draft.saved':
        case 'folders.changed':
          qc.invalidateQueries({ queryKey: ['messages'] });
          qc.invalidateQueries({ queryKey: ['folders'] });
          if (e.data?.id) {
            qc.invalidateQueries({ queryKey: ['message', e.data.id] });
            qc.invalidateQueries({ queryKey: ['thread'] });
          }
          break;
        case 'contacts.changed':
          qc.invalidateQueries({ queryKey: ['contacts'] });
          break;
        case 'prefs.changed':
          qc.invalidateQueries({ queryKey: ['prefs'] });
          break;
        case 'template.changed':
          qc.invalidateQueries({ queryKey: ['templates'] });
          break;
        case 'lists.changed':
          qc.invalidateQueries({ queryKey: ['list'] });
          break;
        case 'test':
          toast.success('Live updates are working', { description: 'Event received from your Storage Queue.' });
          break;
      }
    });
    const offOpen = window.dts.events.onOpen(open);
    return () => {
      offEvent();
      offOpen();
    };
  }, [qc, notifications, setView, select]);
}

/**
 * When a new version has downloaded: restart into it after a short countdown, unless an email is being written —
 * then wait until the composers are closed (it also installs automatically on quit).
 */
/**
 * Updates install silently: once a new version has downloaded, the app restarts into it without asking, but never
 * while a message is being written or two-step verification is being set up. Mounted at the app root so it also
 * runs on the sign-in screen (an old app that can't sign in must still be able to update itself).
 */
function useSilentUpdates() {
  useEffect(() => {
    let started = '';
    const idle = () => {
      const st = useUi.getState();
      return st.composers.length === 0 && !st.signInBusy;
    };
    const installWhenIdle = () => {
      if (idle()) return window.dts.updates.install();
      const unsub = useUi.subscribe(() => {
        if (idle()) {
          unsub();
          window.dts.updates.install();
        }
      });
    };
    const handle = (s: Awaited<ReturnType<typeof window.dts.updates.state>>) => {
      if (s.state === 'ready' && started !== s.version) {
        started = s.version;
        installWhenIdle();
      }
    };
    void window.dts.updates.state().then(handle);
    return window.dts.updates.onState(handle);
  }, []);
}

function useGlobalShortcuts() {
  const setView = useUi((s) => s.setView);
  const setPalette = useUi((s) => s.setPalette);
  const openAsk = useUi((s) => s.openAsk);
  const qc = useQueryClient();
  useEffect(() => {
    const handle = (cmd: string) => {
      if (cmd === 'compose') composeNew();
      else if (cmd === 'compose-ai') composeWithAi();
      else if (cmd === 'palette') setPalette(!useUi.getState().paletteOpen);
      else if (cmd === 'ask') useUi.getState().askOpen ? useUi.getState().closeAsk() : openAsk();
      else if (cmd === 'refresh') qc.invalidateQueries();
      else if (cmd === 'go-today') setView({ kind: 'today' });
      else if (cmd === 'go-inbox') setView({ kind: 'mail', folder: 'Inbox' });
      else if (cmd === 'go-contacts') setView({ kind: 'contacts' });
      else if (cmd === 'go-settings') setView({ kind: 'settings' });
    };
    const offCmd = window.dts.app.onCommand(handle);
    const offMail = window.dts.app.onMailto(composeMailto);
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = t.isContentEditable || /INPUT|TEXTAREA|SELECT/.test(t.tagName);
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'k') (e.preventDefault(), handle('palette'));
      else if (mod && e.key.toLowerCase() === 'j') (e.preventDefault(), handle('ask'));
      else if (!typing && !mod && !e.altKey && e.key === 'c') (e.preventDefault(), composeNew());
      else if (!typing && e.key === 'g') setView({ kind: 'today' });
    };
    window.addEventListener('keydown', onKey);
    return () => {
      offCmd();
      offMail();
      window.removeEventListener('keydown', onKey);
    };
  }, [setView, setPalette, openAsk, qc]);
}

function Shell({ onLogout }: { onLogout: () => void }) {
  const view = useUi((s) => s.view);
  useGlobalShortcuts();
  useEventSync();
  useEffect(() => wireCrmEvents(), []);
  return (
    <div className="relative z-10 flex h-full flex-col">
      <DownloadBar />
      <div className="flex min-h-0 flex-1">
        <Sidebar />
        <main className="flex min-w-0 flex-1 flex-col">
          <TopBar />
          <div className="min-h-0 flex-1">
            {view.kind === 'today' && <Today />}
            {view.kind === 'mail' && <Mail view={view} />}
            {view.kind === 'contacts' && <Contacts />}
            {view.kind === 'campaigns' && <Campaigns view={view} />}
            {view.kind === 'settings' && <Settings tab={view.tab} onLogout={onLogout} />}
          </div>
        </main>
      </div>
      <ComposerDock />
      <CommandPalette />
      <AskPanel />
      <CrmPanel />
      <CrmApprovalDialog />
      <BadgeWatcher />
    </div>
  );
}

export function App() {
  useTheme();
  useSilentUpdates();
  const [session, setSession] = useState<PublicSession | null | undefined>(undefined);
  const theme = useUi((s) => s.local.theme);

  useEffect(() => {
    window.dts.auth.session().then(setSession);
    return window.dts.auth.onExpired(() => {
      setSession(null);
      queryClient.clear();
    });
  }, []);

  const logout = async () => {
    await window.dts.auth.logout();
    queryClient.clear();
    useUi.setState({ composers: [], selectedId: null, view: { kind: 'today' } });
    setSession(null);
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Aurora />
      {session === undefined ? (
        <div className="flex h-full items-center justify-center"><Spinner className="size-6" /></div>
      ) : session === null ? (
        <Login onLogin={(s) => (queryClient.clear(), setSession(s))} />
      ) : (
        <Shell onLogout={logout} />
      )}
      <DialogHost />
      {/* Top right, under the 56px title bar: bottom left covers the sidebar's menu, bottom right the docked composer. */}
      <Toaster position="top-right" offset={{ top: 64, right: 16 }} theme={theme === 'light' ? 'light' : 'dark'} richColors closeButton toastOptions={{ className: 'font-sans' }} />
    </QueryClientProvider>
  );
}
